import { CheckCircle, Circle } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React, { useState } from "react";
import { XStack, YStack } from "tamagui";
import { useStores } from "../../../core/stores";
import { allRoles, Permission } from "../../../permissions/types";
import { Body } from "../../../ui/Body";
import { Box } from "../../../ui/Box";
import { Button } from "../../../ui/Button";
import { Group } from "../../../ui/Group";
import { Separator } from "../../../ui/Separator";
import { Subtext } from "../../../ui/Subtext";
import { Title } from "../../../ui/Title";
import { AvatarSize, UserPropsAvatar } from "../../../ui/UserAvatar";
import type {
  BusinessPlanModel,
  BusinessStaticUserInfoWithUserUid,
} from "../BusinessPlanModel";

type BusinessAdminPlanUsersUI = {
  businessPlanUid: string;
};

const MemberRow = observer(
  ({
    userInfo,
    businessPlan,
    showEmail,
  }: {
    userInfo: BusinessStaticUserInfoWithUserUid;
    businessPlan: BusinessPlanModel;
    showEmail?: boolean;
  }) => {
    const [showEdit, setShowEdit] = useState(false);

    return (
      <YStack key={userInfo.userUid} gap={"$small"} width={"100%"}>
        <XStack
          cursor={"pointer"}
          width={"100%"}
          gap={"$medium"}
          flexWrap={"wrap"}
          onPress={() => {
            setShowEdit(!showEdit);
          }}
          alignItems="center"
        >
          <UserPropsAvatar
            {...userInfo}
            size={AvatarSize.Medium}
            showBorder={businessPlan.checkPermission(
              Permission.AvatarShowExtraBorder,
              userInfo.userUid,
            )}
          />
          <YStack gap="$small">
            <XStack gap={"$small"} alignItems={"flex-end"}>
              <Body fontWeight={"bold"}>{userInfo.displayName}</Body>
              {!!userInfo.roles && (
                <Body muted>{userInfo.roles.join(", ")}</Body>
              )}
            </XStack>
            {showEmail && <Subtext>{userInfo.email}</Subtext>}
          </YStack>
        </XStack>

        {showEdit && (
          <YStack gap={"$medium"}>
            <XStack gap={"$medium"} flexWrap={"wrap"}>
              {allRoles.map((role) => (
                <Button
                  key={role.name}
                  type={"secondary"}
                  onPress={() => {
                    businessPlan.toggleMemberRole(userInfo.userUid, role.name);
                  }}
                >
                  {role.displayName}:{" "}
                  {userInfo.roles.includes(role.name) ? "Yes" : "No"}
                </Button>
              ))}

              <Button
                type={"secondary"}
                onPress={() => {
                  businessPlan.toggleIsVisibleAsMember(userInfo.userUid);
                }}
              >
                Visible: {userInfo.isVisible ? "Yes" : "No"}
              </Button>
              <Button
                type={"secondary"}
                onPress={() => {
                  businessPlan.toggleIsDeleted(userInfo.userUid);
                }}
              >
                Deleted: {userInfo.isDeleted ? "Yes" : "No"}
              </Button>
            </XStack>

            <XStack gap={"$medium"} flexWrap={"wrap"}>
              {businessPlan.data.eventInfo.tags.map((tag) => {
                const hasTag = (userInfo.tags ?? []).find(
                  (tUid) => tUid === tag.uid,
                );

                return (
                  <XStack
                    key={tag.uid}
                    gap={"$small"}
                    alignItems="center"
                    cursor={"pointer"}
                    onPress={() =>
                      businessPlan.toggleTag(userInfo.userUid, tag.uid)
                    }
                  >
                    {hasTag ? <CheckCircle /> : <Circle />}
                    <Body>{tag.name}</Body>
                  </XStack>
                );
              })}
            </XStack>
          </YStack>
        )}
      </YStack>
    );
  },
);

const MembersList = observer(
  ({
    title,
    members,
    businessPlan,
    showEmail,
  }: {
    title: string;
    members: BusinessStaticUserInfoWithUserUid[];
    businessPlan: BusinessPlanModel;
    showEmail?: boolean;
  }) => {
    if (members.length === 0) return null;

    return (
      <>
        <Title>
          {title} ({members.length})
        </Title>
        <Group>
          {members.map((userInfo) => {
            return (
              <MemberRow
                key={userInfo.userUid}
                userInfo={userInfo}
                businessPlan={businessPlan}
                showEmail={showEmail}
              />
            );
          })}
        </Group>

        <Separator />
      </>
    );
  },
);

export const BusinessAdminPlanUsersUI = observer(
  ({ businessPlanUid }: BusinessAdminPlanUsersUI) => {
    const { businessPlansStore } = useStores();

    const businessPlan = businessPlansStore.byId.get(businessPlanUid);

    if (
      !businessPlan ||
      !businessPlan.checkPermission(Permission.AccessToAdminPage)
    ) {
      return null;
    }

    return (
      <Box>
        <YStack gap={"$medium"}>
          <MembersList
            title={"Members"}
            members={businessPlan.allUsersSorted}
            businessPlan={businessPlan}
            showEmail
          />
        </YStack>
      </Box>
    );
  },
);
